import { useQuery } from '@tanstack/react-query'

export interface OccupancyMapBerth {
  id: string
  code: string
  number: number
  pontoonId: string
  pontoon?: { code: string; name: string | null }
  maxLoaMeters: string
  maxBeamMeters: string
  maxDraftMeters: string
  depthMeters: string
  dailyRateIdr: string
  mapX: number | null
  mapY: number | null
  mapWidth: number | null
  mapHeight: number | null
  mapRotation: number
  status: string
  currentBooking: {
    id: string
    code: string
    status: string
    checkInAt: string
    checkOutAt: string
    customer: { id: string; firstName: string; lastName: string; category: string }
    boat: { id: string; name: string }
  } | null
}

export interface OccupancyMapResponse {
  date: string
  pontoons: { id: string; code: string; name: string | null; sortOrder: number }[]
  berths: OccupancyMapBerth[]
}

async function apiFetch<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'include' })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

export function useOccupancyMap(date?: string) {
  const q = date ? `?date=${encodeURIComponent(date)}` : ''
  return useQuery({
    queryKey: ['occupancy', 'map', date],
    queryFn: () => apiFetch<OccupancyMapResponse>(`/api/occupancy/map${q}`),
    staleTime: 15_000,
  })
}

export interface OccupancyCalendarResponse {
  from: string
  to: string
  berths: { id: string; code: string; pontoonId: string }[]
  bookings: {
    id: string
    code: string
    status: string
    berthId: string
    checkInAt: string
    checkOutAt: string
    customer: { firstName: string; lastName: string }
    boat: { name: string }
  }[]
}

export function useOccupancyCalendar(params: { from: string; to: string; pontoonId?: string }) {
  const qs = new URLSearchParams({ from: params.from, to: params.to })
  if (params.pontoonId) qs.set('pontoonId', params.pontoonId)
  return useQuery({
    queryKey: ['occupancy', 'calendar', params],
    queryFn: () => apiFetch<OccupancyCalendarResponse>(`/api/occupancy/calendar?${qs}`),
    staleTime: 30_000,
  })
}

export interface ArrivalDepartureItem {
  id: string
  code: string
  kind: 'arrival' | 'departure'
  status: string
  customer: { id: string; firstName: string; lastName: string; category: string; email?: string | null }
  boat: { id: string; name: string; type?: string; loaMeters?: string }
  berth: { id: string; code: string } | null
  scheduledAt: string
  actualAt: string | null
  checkInAt: string
  checkOutAt: string
}

export function useArrivalsDepartures(params: { date: string; type?: 'all' | 'arrivals' | 'departures' }) {
  const qs = new URLSearchParams({ date: params.date, type: params.type ?? 'all' })
  return useQuery({
    queryKey: ['occupancy', 'arrivals-departures', params],
    queryFn: () =>
      apiFetch<{
        date: string
        arrivals: ArrivalDepartureItem[]
        departures: ArrivalDepartureItem[]
      }>(`/api/occupancy/arrivals-departures?${qs}`),
    staleTime: 30_000,
  })
}

export interface AbsenceItem {
  id: string
  bookingId: string
  berthId: string
  departure: string
  returnDate: string
  reason: string | null
  status: 'AVAILABLE' | 'BLOCKED'
  createdAt: string
  berth: { code: string; maxLoaMeters: string; maxBeamMeters: string; maxDraftMeters: string }
  booking: {
    id: string
    code: string
    customer: { id: string; firstName: string; lastName: string; category: string }
  }
}

export function useOccupancyAbsences(params: { from?: string; to?: string; status?: 'AVAILABLE' | 'BLOCKED' } = {}) {
  const qs = new URLSearchParams()
  if (params.from) qs.set('from', params.from)
  if (params.to) qs.set('to', params.to)
  if (params.status) qs.set('status', params.status)
  return useQuery({
    queryKey: ['occupancy', 'absences', params],
    queryFn: () =>
      apiFetch<{ items: AbsenceItem[]; total: number }>(`/api/occupancy/absences${qs.toString() ? `?${qs}` : ''}`),
    staleTime: 30_000,
  })
}
